import React, { Dispatch, FC, ReactNode } from 'react';
import {
  Button,
  InputAdornment,
  GlobalStyles,
  InputLabelProps as MuiInputLabelProps,
  OutlinedInputProps,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import NumberField from '../hook_form_fields/NumberField';
import { Control } from 'react-hook-form';

type AmountInputProps<T extends string = any> = {
  /**Optional if used inside react-hook-form FormProvider */
  control?: Control<any>;
  name: string;
  activeAmountInput: string;
  setActiveAmountInput: Dispatch<T>;
  startAdornmentText?: string;
  label: ReactNode;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  showError?: boolean;
  warningText?: ReactNode;
  helperText?: ReactNode;
  allowNegative?: boolean;
  touched?: boolean;
  InputLabelProps?: Partial<MuiInputLabelProps>;
  InputProps?: Partial<OutlinedInputProps>;
};

const AmountInput: FC<AmountInputProps> = ({
  control,
  name,
  activeAmountInput,
  setActiveAmountInput,
  label,
  startAdornmentText,
  onBlur,
  showError, // This prop is for the case where we don't want to display the validator's error message. It traverses down to the NumberField component not to render it if true
  warningText,
  helperText,
  allowNegative = true,
  touched,
  InputLabelProps,
  InputProps,
  ...rest
}) => {
  const isActive = activeAmountInput === name;

  return (
    <>
      <GlobalStyles
        // There are some browsers that come with something called 'user agent stylesheet'. That means they add their own styling/behaviour.
        // The only way to override that using MUI, we need to import GlobalStyles and target the specific selectors
        styles={{
          /* For Chrome, Safari, and Edge */
          ...(isActive
            ? {
                'input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0px 1000px #F9FBFD inset',
                },
                'input:disabled:-webkit-autofill': {
                  backgroundColor: '#E5EEF6',
                },
                '.MuiInputBase-inputAdornedEnd:-webkit-autofill': {
                  // Here I'm targeting the input, but the one that gets added the inputAdornedEnd (which is what appears when disabled)
                  WebkitBoxShadow: '0 0 0px 1000px #E5EEF6 inset',
                },
              }
            : {}),
        }}
      />
      <NumberField
        control={control}
        name={name}
        onBlur={onBlur}
        sx={{
          border: '2px solid #DCE1ED',
          borderRadius: '6px',
          p: 1.75,
          ...(activeAmountInput === name
            ? {
                backgroundColor: 'none',
                border: '2px solid #2E9C7F',
              }
            : {}),
        }}
        disabled={activeAmountInput !== name}
        fullWidth
        decimalScale={2}
        allowNegative={allowNegative}
        thousandSeparator
        startAdornmentText={startAdornmentText}
        InputProps={{
          ...(activeAmountInput !== name
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      variant="text"
                      color="primary"
                      startIcon={<Edit />}
                      onClick={() => {
                        setActiveAmountInput(name);
                      }}
                      sx={{
                        p: 0,
                      }}
                    >
                      Edit
                    </Button>
                  </InputAdornment>
                ),
              }
            : undefined),
          ...InputProps,
        }}
        label={label}
        InputLabelProps={InputLabelProps}
        showError={showError}
        touched={touched}
        warningText={warningText}
        helperText={helperText}
        {...rest}
      />
    </>
  );
};

export default AmountInput;
